<template>
  <div v-if="post && typeof post === 'object' && Object.keys(post).length > 0" class="post-card" @click="handlePostClick" style="cursor:pointer;">
    <div class="post-header">
      <div class="user-info user-info-horizontal">
  <div class="user-avatar" @click.stop="goToUserProfile">
          <img 
            v-if="post.usuario && post.usuario.fotoPerfil" 
            :src="post.usuario.fotoPerfil" 
            :alt="post.usuario.nickname || post.usuarioNickname || post.usuarioNombre"
          />
          <div v-else class="default-avatar">
            {{ getInitials((post.usuario && post.usuario.nickname) || post.usuarioNickname || post.usuarioNombre) }}
          </div>
        </div>
        <div class="user-details-horizontal">
          <span class="user-realname">{{ (post.usuario && post.usuario.nombre) || post.usuarioNombre || 'Usuario' }}</span>
          <span class="user-nickname">@{{ (post.usuario && post.usuario.nickname) || post.usuarioNickname || post.usuarioNombre || 'nickname' }}</span>
        </div>
      </div>
      <div class="post-actions" v-if="canManagePost">
        <Button 
          icon="pi pi-ellipsis-v" 
          class="p-button-text p-button-sm"
          @click="toggleMenu"
        />
        <div v-if="showMenu" class="action-menu">
          <button @click="editPost" class="menu-item">
            <i class="pi pi-pencil"></i> Editar
          </button>
          <button @click="deletePost" class="menu-item delete">
            <i class="pi pi-trash"></i> Eliminar
          </button>
        </div>
      </div>
    </div>

    <div class="post-content">
      <p v-if="post.contenido" class="content-text">{{ post.contenido }}</p>
      
      <div v-if="hasMultimedia" class="media-content">
        <div class="media-grid">
          <div 
            v-for="(image, index) in allImages" 
            :key="'img-' + index"
            class="media-item"
            @click="openMediaModal(image, 'image')"
          >
            <img :src="image" :alt="`Imagen ${index + 1}`" />
          </div>
          <div 
            v-for="(video, index) in allVideos" 
            :key="'vid-' + index"
            class="media-item video-item"
          >
            <video :src="video" controls preload="metadata">
              Tu navegador de pobres no soporta el elemento video.
            </video>
          </div>
        </div>
      </div>
    </div>

    <div class="post-footer">
      <div class="post-stats-date-wrapper">
        <div class="post-stats">
          <Button 
            icon="pi pi-comment" 
            :label="commentCount.toString()"
            class="p-button-text stat-button"
            @click.stop="toggleComments"
          />
          <Button 
            :icon="isLiked ? 'pi pi-heart-fill' : 'pi pi-heart'"
            :label="likeCount.toString()"
            class="p-button-text stat-button like-btn"
            :severity="isLiked ? 'danger' : 'secondary'"
            @click.stop="handleLike"
          />
          <Button 
            :icon="isDisliked ? 'pi pi-thumbs-down-fill' : 'pi pi-thumbs-down'"
            :label="dislikeCount.toString()"
            class="p-button-text stat-button dislike-btn"
            :severity="isDisliked ? 'danger' : 'secondary'"
            @click.stop="handleDislike"
          />
          <Button 
            icon="pi pi-share-alt" 
            class="p-button-text stat-button"
            @click="openShareModal"
          />
        </div>
        <span class="post-date">{{ formatPostDate(post.fechaCreacion) || 'Sin fecha' }}</span>
      </div>
      <div v-if="shareModalVisible" class="share-inline-wrapper">
        <div class="share-inline-content">
          <input class="share-link-input" :value="shareLink" readonly />
          <Button label="Copiar" class="copy-btn" @click="copyShareLink" />
        </div>
        <span v-if="copied" class="copied-msg">¡Copiado!</span>
      </div>
    </div>

    <!-- Sección de comentarios -->
    <div v-if="showComments" class="comments-section">
      <div class="add-comment">
  <CreatePost :postPadreId="post.id" @postCreated="onCommentCreated" />
      </div>
      
      <div v-if="comments.length > 0" class="comments-list">
        <CommentThread :comments="comments" :level="0" />
      </div>
      
      <div v-else-if="showComments" class="no-comments">
        <p>No hay comentarios aún. ¡Sé el primero en comentar!</p>
      </div>
    </div>

    <!-- Modal para ver media en pantalla completa -->
    <Dialog 
      v-model:visible="mediaModalVisible" 
      modal 
      :style="{ width: '80vw', maxWidth: '1200px', background: 'transparent' }"
      :showHeader="false"
      contentStyle="background: transparent; box-shadow: none;"
    >
      <div class="media-modal">
        <button class="close-modal-btn" @click="mediaModalVisible = false">✕</button>
        <img 
          v-if="selectedMedia.type === 'image'" 
          :src="selectedMedia.url" 
          class="modal-media-image"
        />
        <video 
          v-else-if="selectedMedia.type === 'video'" 
          :src="selectedMedia.url" 
          controls 
          class="modal-media"
        >
        </video>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, defineComponent, h } from 'vue';
import { postService } from '@/services/apiService';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import CreatePost from './CreatePost.vue';
// Recursive comment thread component
const CommentThread = defineComponent({
  name: 'CommentThread',
  props: {
    comments: { type: Array, required: true },
    level: { type: Number, default: 0 },
    formatDate: { type: Function, default: null },
    formatPostDate: { type: Function, default: null }
  },
  setup(props) {
    const getDate = (date) => {
      if (typeof props.formatDate === 'function') return props.formatDate(date);
      if (typeof props.formatPostDate === 'function') return props.formatPostDate(date);
      return '';
    };
    return () => props.comments.map(comment =>
      h('div', {
        class: 'comment-item',
        style: { marginLeft: `${props.level * 24}px` }
      }, [
        h('div', { class: 'comment-header' }, [
          h('span', { class: 'comment-author' }, `@${comment.usuarioNickname}`),
          h('span', { class: 'comment-date' }, getDate(comment.fechaCreacion))
        ]),
        h('p', { class: 'comment-content' }, comment.contenido),
        (Array.isArray(comment.replies) && comment.replies.length > 0) || (Array.isArray(comment.comentarios) && comment.comentarios.length > 0)
          ? h(CommentThread, {
              comments: comment.replies || comment.comentarios,
              level: props.level + 1,
              formatDate: props.formatDate,
              formatPostDate: props.formatPostDate
            })
          : null
      ])
    );
  }
});

export default {
  name: 'PostCard',
  components: {
    Button,
    Dialog,
    CommentThread,
    CreatePost
  },
  props: {
    post: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  emits: ['openPost', 'postDeleted', 'commentAdded'],
  setup(props, { emit }) {
    // Manejar la creación de un comentario desde CreatePost
    const onCommentCreated = (newComment) => {
      comments.value.push(newComment);
      emit('commentAdded', newComment);
    };

    // Definición de variables reactivas usadas en el return
    const showMenu = ref(false);
    const showComments = ref(false);
    const comments = ref([]);
    const newComment = ref('');
    const isCommenting = ref(false);
    const mediaModalVisible = ref(false);
    const selectedMedia = ref({ url: '', type: '' });
    const canManagePost = computed(() => {
      const currentUser = localStorage.getItem('userNickname');
      return currentUser === (props.post?.usuario?.nickname || props.post?.usuarioNickname);
    });
    const allImages = computed(() => {
      return props.post?.imagenesUrls?.length > 0
        ? props.post.imagenesUrls
        : (props.post?.imagenes || []);
    });
    const allVideos = computed(() => {
      return props.post?.videosUrls?.length > 0
        ? props.post.videosUrls
        : (props.post?.videos || []);
    });
    const hasMultimedia = computed(() => {
      return allImages.value.length > 0 || allVideos.value.length > 0;
    });
    // Permite emitir el evento para abrir el detalle
    // Redirige al perfil del usuario
    const goToUserProfile = () => {
      if (props.post?.usuario?.nickname) {
        window.location.href = `/perfil/${props.post.usuario.nickname}`;
      } else if (props.post?.usuarioNickname) {
        window.location.href = `/perfil/${props.post.usuarioNickname}`;
      }
    };

    // Click general en el post
    const handlePostClick = (e) => {
      // Si el click fue en un botón de acción, avatar, comentarios, etc, no navegar
      if (
        e.target.closest('.post-actions') ||
        e.target.closest('.btn-comment') ||
        e.target.closest('.add-comment') ||
        e.target.closest('.comments-section') ||
        e.target.closest('.user-avatar')
      ) return;
      emit('openPost', props.post);
    };

    const getInitials = (nickname) => {
      if (!nickname) return '?';
      return nickname.substring(0, 2).toUpperCase();
    };

    // ...resto del setup...

  // ...no return aquí, solo el return final al final del setup...

    // Formatea la fecha de creación del post considerando zona horaria America/Chicago
    function formatPostDate(fechaCreacion) {
      if (!fechaCreacion) return '';
      let date;
      if (typeof fechaCreacion === 'string') {
        date = new Date(fechaCreacion + 'Z');
      } else {
        date = fechaCreacion;
      }
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'justo ahora';
      if (diffMin < 60) return `hace ${diffMin} min`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `hace ${diffH} h`;
      const localDate = date;
      const localNow = now;
      const isToday = localDate.toDateString() === localNow.toDateString();
      const yesterday = new Date(localNow);
      yesterday.setDate(localNow.getDate() - 1);
      const isYesterday = localDate.toDateString() === yesterday.toDateString();
      if (isToday) {
        return 'hoy, ' + localDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      }
      if (isYesterday) {
        return 'ayer, ' + localDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
      }
      return localDate.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) + ' (CDT)';
    }

    // Helper for comment date formatting in template

    // currentUser para uso en deletePost
    const currentUser = localStorage.getItem('userNickname');

    // commentCount: recuento de comentarios (puede ser plano o recursivo)
    function countAllComments(commentsArr) {
      if (!Array.isArray(commentsArr)) return 0;
      return commentsArr.reduce((acc, comment) => {
        return acc + 1 + countAllComments(comment.replies || comment.comentarios || []);
      }, 0);
    }
    const commentCount = computed(() => countAllComments(comments.value));

    // toggleMenu y toggleComments
    const toggleMenu = () => { showMenu.value = !showMenu.value; };
    const toggleComments = async () => {
      showComments.value = !showComments.value;
      if (showComments.value && comments.value.length === 0) {
        await loadComments();
      }
    };

    // ...existing code...

  // (return eliminado, solo dejar el return final al final del setup)

    const addComment = async () => {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!newComment.value.trim() || isCommenting.value) return;
      if (!token || !userId) return;
      try {
        isCommenting.value = true;
        // Crear FormData para el comentario
        const formData = new FormData();
        formData.append('contenido', newComment.value.trim());
        formData.append('usuarioId', userId);
        formData.append('postPadreId', props.post.id != null ? props.post.id.toString() : '');
        const newCommentObj = await postService.createPost(formData, token);
        comments.value.push(newCommentObj);
        newComment.value = '';
        emit('commentAdded', props.post.id, newCommentObj);
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        isCommenting.value = false;
      }
    };

    const editPost = () => {
      // TODO: Implementar edición de posts
      console.log('Edit post:', props.post.id);
      showMenu.value = false;
    };

    const deletePost = async () => {
      if (!confirm('¿Estás seguro de que quieres eliminar este post?')) return;

      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        await postService.deletePost(props.post.id, currentUser, token);
        emit('postDeleted', props.post.id);
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Error al eliminar el post: ' + error.message);
      } finally {
        showMenu.value = false;
      }
    };


    // --- Like/Dislike State ---
    const isLiked = ref(false);
    const isDisliked = ref(false);
    const likeCount = ref(0);
    const dislikeCount = ref(0);

    const fetchLikeDislikeStatus = async () => {
      // Si el post es un comentario (tiene postPadre), no consultar likes/dislikes
      if (props.post.postPadre) return;
      const userId = localStorage.getItem('userId');
      try {
        const status = await postService.getLikeDislikeStatus(props.post.id);
        // likes y dislikes son arrays de objetos Like/Dislike
        likeCount.value = status.likes.length;
        dislikeCount.value = status.dislikes.length;
        // Determinar si el usuario actual ha dado like/dislike
        isLiked.value = !!userId && status.likes.some(like => like.usuario && (like.usuario.id == userId));
        isDisliked.value = !!userId && status.dislikes.some(dislike => dislike.usuario && (dislike.usuario.id == userId));
      } catch (e) {
        likeCount.value = 0;
        dislikeCount.value = 0;
        isLiked.value = false;
        isDisliked.value = false;
      }
    };

    const fetchLikeDislikeCounts = async () => {
      // Si el post es un comentario (tiene postPadre), no consultar likes/dislikes
      if (props.post.postPadre) return;
      try {
        const counts = await postService.getLikeDislikeCounts(props.post.id);
        likeCount.value = typeof counts.likes === 'number' ? counts.likes : 0;
        dislikeCount.value = typeof counts.dislikes === 'number' ? counts.dislikes : 0;
      } catch (e) {
        likeCount.value = 0;
        dislikeCount.value = 0;
      }
    };

    const handleLike = async () => {
      // Si el post es un comentario (tiene postPadre), no permitir like
      if (props.post.postPadre) return;
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) return;
      try {
        await postService.likePost(props.post.id, userId, token);
        await fetchLikeDislikeStatus();
        await fetchLikeDislikeCounts();
      } catch (e) {
        alert('Error al dar like: ' + (e.message || e));
      }
    };

    const handleDislike = async () => {
      // Si el post es un comentario (tiene postPadre), no permitir dislike
      if (props.post.postPadre) return;
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!token || !userId) return;
      try {
        await postService.dislikePost(props.post.id, userId, token);
        await fetchLikeDislikeStatus();
        await fetchLikeDislikeCounts();
      } catch (e) {
        alert('Error al dar dislike: ' + (e.message || e));
      }
    };

    // --- Compartir post ---
    const shareModalVisible = ref(false);
    const copied = ref(false);
    const shareLink = computed(() => {
      // Puedes ajustar la URL base según tu despliegue
      return `${window.location.origin}/post/${props.post.id}`;
    });
    const openShareModal = (e) => {
      if (e) e.stopPropagation();
      copied.value = false;
      shareModalVisible.value = !shareModalVisible.value;
    };
    const copyShareLink = async () => {
      try {
        await navigator.clipboard.writeText(shareLink.value);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 1500);
      } catch (e) {
        copied.value = false;
        alert('No se pudo copiar el enlace');
      }
    };

    // Cargar comentarios desde el backend
    const loadComments = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      try {
        const response = await postService.getPostComments(props.post.id, token);
        comments.value = response || [];
      } catch (error) {
        console.error('Error loading comments:', error);
      }
    };

    const openMediaModal = (url, type) => {
      selectedMedia.value = { url, type };
      mediaModalVisible.value = true;
    };

    // Cerrar menú al hacer clic fuera
    const handleClickOutside = (event) => {
      if (!event.target.closest('.post-actions')) {
        showMenu.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      fetchLikeDislikeStatus();
      fetchLikeDislikeCounts();
    });

    return {
      showMenu,
      showComments,
      comments,
      newComment,
      isCommenting,
      mediaModalVisible,
      selectedMedia,
      canManagePost,
      hasMultimedia,
      commentCount,
      getInitials,
      formatPostDate,
      toggleMenu,
      toggleComments,
      addComment,
      editPost,
      deletePost,
      isLiked,
      isDisliked,
      likeCount,
      dislikeCount,
      handleLike,
      handleDislike,
      openShareModal,
      shareModalVisible,
      shareLink,
      copyShareLink,
      copied,
      openMediaModal,
      goToUserProfile,
      handlePostClick,
      allImages,
  allVideos,
  onCommentCreated
    };

  }
};
</script>

<style scoped>
.post-card {
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e8ed;
  transition: box-shadow 0.2s;
}

.post-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}
.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #6a0dad;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.default-avatar {
  color: white;
  font-weight: bold;
  font-size: 14px;
}

/* Nombre grande, nickname pequeño debajo, ambos a la derecha del avatar */
.user-info-horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.user-details-horizontal {
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
}
.user-realname {
  font-size: 1.2rem;
  font-weight: 700;
  color: #222;
  margin-bottom: 0.1rem;
}
.user-nickname {
  font-size: 0.95rem;
  color: #6a0dad;
  font-weight: 500;
  margin-bottom: 0;
}


.post-stats-date-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.post-date {
  font-size: 13px;
  color: #666;
  margin-left: 1.5rem;
  white-space: nowrap;
}

/* Modal compartir */

/* Compartir inline debajo del post */
.share-inline-wrapper {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.share-inline-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}
.share-link-input {
  flex: 1;
  min-width: 0;
  padding: 0.5rem;
  border: 1px solid #e1e8ed;
  border-radius: 6px;
  font-size: 15px;
  background: #f8f9fa;
  color: #333;
}
.copy-btn {
  background-color: #6a0dad;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 15px;
  cursor: pointer;
}
.copy-btn:hover {
  background-color: #8a2be2;
}
.copied-msg {
  color: #6a0dad;
  font-size: 13px;
  margin-top: 0.25rem;
  text-align: left;
}

.post-actions {
  position: relative;
}

.action-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 120px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  border: none;
  background: none;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background: #f8f9fa;
}

.menu-item.delete {
  color: #ff4444;
}

.menu-item.delete:hover {
  background: #fff5f5;
}

.post-content {
  margin-bottom: 1rem;
}

.content-text {
  margin: 0 0 1rem 0;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.media-content {
  margin-top: 1rem;
}

.media-grid {
  display: grid;
  gap: 0.5rem;
  border-radius: 12px;
  overflow: hidden;
}

.media-grid:has(.media-item:nth-child(1):nth-last-child(1)) {
  grid-template-columns: 1fr;
}

.media-grid:has(.media-item:nth-child(1):nth-last-child(2)) {
  grid-template-columns: 1fr 1fr;
}

.media-grid:has(.media-item:nth-child(1):nth-last-child(3)) {
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.media-grid:has(.media-item:nth-child(1):nth-last-child(3)) .media-item:first-child {
  grid-row: 1 / 3;
}

.media-grid:has(.media-item:nth-child(1):nth-last-child(4)) {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.media-item {
  position: relative;
  cursor: pointer;
  aspect-ratio: 16/9;
  overflow: hidden;
  background: #f0f0f0;
}

.media-item img,
.media-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}

.media-item:hover img {
  transform: scale(1.02);
}

.video-item {
  cursor: default;
}

.video-item video {
  object-fit: contain;
}

.post-footer {
  border-top: 1px solid #e1e8ed;
  padding-top: 1rem;
}

.post-stats {
  display: flex;
  gap: 1rem;
}

.stat-button {
  color: #666 !important;
  font-size: 14px;
}

.stat-button:hover {
  color: #6a0dad !important;
}

.comments-section {
  margin-top: 1rem;
  border-top: 1px solid #e1e8ed;
  padding-top: 1rem;
}

.add-comment {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.comment-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 40px;
  max-height: 120px;
}

.comment-input:focus {
  outline: none;
  border-color: #6a0dad;
}

.btn-comment {
  background-color: #6a0dad;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.btn-comment:hover:not(:disabled) {
  background-color: #8a2be2;
}

.btn-comment:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.comment-item {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 8px;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.comment-author {
  font-weight: 600;
  font-size: 13px;
  color: #6a0dad;
}

.comment-date {
  font-size: 11px;
  color: #666;
}

.comment-content {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: #333;
}

.no-comments {
  text-align: center;
  color: #666;
  font-size: 14px;
  padding: 1rem;
}

.media-modal {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.modal-media-image {
  max-width: 80vw;
  max-height: 80vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.25);
  background: white;
}

.close-modal-btn {
  position: absolute;
  top: 18px;
  right: 24px;
  background: rgba(0,0,0,0.7);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.close-modal-btn:hover {
  background: rgba(0,0,0,0.9);
}

/* Responsive */
@media (max-width: 768px) {
  .post-card {
    padding: 1rem;
  }
  
  .add-comment {
    flex-direction: column;
  }
  
  .post-stats {
    justify-content: space-around;
  }
}
</style>
